/**
 * GRACE/GRACE-FO Data API Service
 * Fetches real gravity anomaly data from NASA PO.DAAC
 */

// Base URLs for different data sources
const DATA_SOURCES = {
  JPL_MASCON: 'https://podaac-tools.jpl.nasa.gov/podaac/data/grace',
  CSR_MASCON: 'https://www2.csr.utexas.edu/grace/RL06_mascons',
  TELLUS: 'https://grace.jpl.nasa.gov/api/data',
  PODAAC_OPENDAP: 'https://podaac-opendap.jpl.nasa.gov/opendap/allData/grace'
};

// Available data products with their specifications
const PRODUCTS = {
  land_mascon: {
    name: 'JPL GRACE Mascon Land',
    path: 'mascon_land',
    resolution: '0.5deg',
    unit: 'cm water equivalent',
    source: DATA_SOURCES.JPL_MASCON,
    version: 'RL06'
  },
  ocean_mascon: {
    name: 'JPL GRACE Mascon Ocean',
    path: 'mascon_ocean',
    resolution: '0.5deg',
    unit: 'cm water equivalent',
    source: DATA_SOURCES.JPL_MASCON,
    version: 'RL06'
  },
  global_timeseries: {
    name: 'Global Time Series',
    path: 'timeseries',
    resolution: 'global',
    unit: 'cm',
    source: DATA_SOURCES.TELLUS,
    version: 'RL06'
  }
};

// NASA Earthdata Login credentials (you'll need to register)
// Store these in environment variables
const NASA_EARTHDATA_USERNAME = process.env.REACT_APP_NASA_USERNAME || '';
const NASA_EARTHDATA_PASSWORD = process.env.REACT_APP_NASA_PASSWORD || '';

/**
 * Main API class for GRACE data
 */
class GRACEAPI {
  constructor() {
    this.cache = new Map();
    this.yearCache = new Map();
    this.rateLimit = 1000; // ms between requests
    this.lastRequest = 0;
    this.authToken = null;
    this.tokenExpiry = null;
  }

  /**
   * Rate limiting helper
   */
  async waitForRateLimit() {
    const now = Date.now();
    const timeSinceLast = now - this.lastRequest;
    if (timeSinceLast < this.rateLimit) {
      await new Promise(resolve => setTimeout(resolve, this.rateLimit - timeSinceLast));
    }
    this.lastRequest = Date.now();
  }

  /**
   * Authenticate with NASA Earthdata Login
   */
  async authenticate() {
    // Check if we have a valid token
    if (this.authToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.authToken;
    }

    try {
      // Basic authentication for NASA Earthdata Login
      const response = await fetch('https://urs.earthdata.nasa.gov/api/users/token', {
        method: 'POST',
        headers: {
          'Authorization': 'Basic ' + btoa(`${NASA_EARTHDATA_USERNAME}:${NASA_EARTHDATA_PASSWORD}`),
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      const data = await response.json();
      this.authToken = data.access_token;
      this.tokenExpiry = Date.now() + (data.expires_in * 1000);
      
      return this.authToken;
    } catch (error) {
      console.warn('Authentication failed, continuing with public access:', error);
      return null;
    }
  }

  /**
   * Get authentication headers
   */
  async getAuthHeaders() {
    const token = await this.authenticate();
    if (token) {
      return {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json, application/x-netcdf, application/octet-stream'
      };
    }
    return {
      'Accept': 'application/json, application/x-netcdf, application/octet-stream'
    };
  }

  /**
   * Fetch GRACE mascon data for a specific year and month
   * @param {number} year - Year (2002-2023)
   * @param {number} month - Month (1-12)
   * @param {string} product - Product type ('land_mascon', 'ocean_mascon', 'global_timeseries')
   * @param {Object} options - Fetch options (signal for abort)
   * @returns {Promise<Object>} Gravity anomaly data
   */
  async fetchMonthlyData(year, month, product = 'land_mascon', options = {}) {
    const cacheKey = `${product}_${year}_${month}`;
    
    // Check cache first
    if (this.cache.has(cacheKey)) {
      console.log('Returning cached data for', cacheKey);
      return this.cache.get(cacheKey);
    }

    await this.waitForRateLimit();

    try {
      let data;
      switch (product) {
        case 'land_mascon':
          data = await this.fetchJPLMascon(year, month, 'land', options);
          break;
        case 'ocean_mascon':
          data = await this.fetchJPLMascon(year, month, 'ocean', options);
          break;
        case 'global_timeseries':
          data = await this.fetchGlobalTimeseries(year, month, options);
          break;
        default:
          throw new Error(`Unknown product: ${product}`);
      }

      // Cache the result
      this.cache.set(cacheKey, data);
      
      // Limit cache size
      if (this.cache.size > 50) {
        const firstKey = this.cache.keys().next().value;
        this.cache.delete(firstKey);
      }

      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      console.error('Error fetching GRACE data:', error);
      // Fall back to simulated data
      return this.generateFallbackData(year, month, product);
    }
  }

  /**
 * Fetch JPL MASCON data with abort support
 */
async fetchJPLMascon(year, month, type, options = {}) {
  const monthStr = month.toString().padStart(2, '0');
  const { signal } = options;
  
  // Try different possible filename patterns
  const filenamePatterns = [
    `GRACE_${year}-${monthStr}_${type}_mascon.nc`,
    `GRACE_${year}-${monthStr}_${type}_mascon_v02.nc`,
    `GRACE_${year}-${monthStr}_${type}_mascon_v03.nc`,
    `GRACE_${year}-${monthStr}_${type}_mascon_v04.nc`,
    `GRACE_${year}-${month}_${type}_mascon.nc`
  ];

  const headers = await this.getAuthHeaders();

  // Try each pattern
  for (const filename of filenamePatterns) {
    // Check if aborted
    if (signal?.aborted) {
      throw new Error('AbortError');
    }
    
    const url = `${DATA_SOURCES.JPL_MASCON}/${year}/${filename}`;
    
    try {
      const response = await fetch(url, { 
        headers, 
        signal 
      });

      if (response.ok) {
        const arrayBuffer = await response.arrayBuffer();
        return this.parseNetCDF(arrayBuffer, type, { year, month });
      }
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      // Continue to next pattern
    }
  }

  // Check if aborted before fallback
  if (signal?.aborted) {
    throw new Error('AbortError');
  }

  // Try alternative source (Tellus)
  return this.fetchTellusMascon(year, month, type, options);
}

/**
 * Fetch Tellus mascon data with abort support
 */
async fetchTellusMascon(year, month, type, options = {}) {
  const { signal } = options;
  const monthStr = month.toString().padStart(2, '0');
  const url = `${DATA_SOURCES.TELLUS}/mascon/${type}`;
  
  const params = new URLSearchParams({
    'time': `${year}-${monthStr}`,
    'format': 'json'
  });

  // Check if aborted
  if (signal?.aborted) {
    throw new Error('AbortError');
  }

  try {
    const response = await fetch(`${url}?${params}`, { 
      signal 
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return this.formatMasconData(data, type, { year, month });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    console.warn(`Tellus fetch failed for ${year}-${month}, using fallback`);
    
    // Check if aborted before fallback
    if (signal?.aborted) {
      throw new Error('AbortError');
    }
    
    return this.generateFallbackData(year, month, type);
  }
}

  /**
   * Fetch global time series data
   */
  async fetchGlobalTimeseries(year, month, options = {}) {
    const url = `${DATA_SOURCES.TELLUS}/mascon/global`;
    
    const params = new URLSearchParams({
      'time-start': `${year}-${month.toString().padStart(2, '0')}-01`,
      'time-end': `${year}-${month.toString().padStart(2, '0')}-31`,
      'format': 'json'
    });

    try {
      const response = await fetch(`${url}?${params}`, { 
        signal: options.signal 
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return this.formatTimeseriesData(data, { year, month });
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      console.error('Error fetching timeseries:', error);
      throw error;
    }
  }

  /**
   * Format mascon data for the app
   */
  formatMasconData(data, type, { year, month }) {
    // Convert Tellus format to app format
    const result = [];
    
    if (data && Array.isArray(data)) {
      data.forEach(point => {
        if (point.lat && point.lon && point.value !== undefined) {
          result.push({
            lat: point.lat,
            lng: point.lon,
            value: point.value,
            uncertainty: point.uncertainty || 0,
            date: `${year}-${month.toString().padStart(2, '0')}`
          });
        }
      });
    }
    
    return result;
  }

  /**
   * Parse NetCDF data (simplified - would need actual NetCDF parser)
   */
  parseNetCDF(arrayBuffer, type, { year, month }) {
    // In production, use a NetCDF library like 'netcdfjs'
    // This is a placeholder that returns simulated data
    console.log('NetCDF parsing would happen here');
    
    // Return simulated data as fallback
    return this.generateFallbackData(year, month, type);
  }

  /**
   * Format timeseries data for your app
   */
  formatTimeseriesData(data, { year, month }) {
    return {
      type: 'timeseries',
      values: data.map(point => ({
        date: point.time || `${year}-${month.toString().padStart(2, '0')}`,
        value: point.anomaly || point.value || 0,
        uncertainty: point.uncertainty || 0
      })),
      metadata: {
        source: 'GRACE Tellus',
        unit: 'cm',
        region: 'global',
        date: `${year}-${month.toString().padStart(2, '0')}`
      }
    };
  }

  /**
   * Generate fallback simulated data when real data isn't available
   */
  generateFallbackData(year, month, type) {
    const data = [];
    const resolution = 2; // degrees
    
    // Determine base value based on type
    let baseValue = 0;
    if (type === 'land' || type === 'land_mascon') baseValue = 0;
    else if (type === 'ocean' || type === 'ocean_mascon') baseValue = -5;
    else if (type === 'global_timeseries') baseValue = 2;
    
    for (let lat = -90; lat <= 90; lat += resolution) {
      for (let lng = -180; lng <= 180; lng += resolution) {
        let value = baseValue;
        
        // Add realistic geophysical patterns
        const yearFactor = (year - 2002) * 0.3;
        
        // Greenland ice loss
        if (lat > 60 && lat < 80 && lng > -60 && lng < -20) {
          value = -15 - yearFactor * 0.5;
        }
        // Antarctica ice loss  
        else if (lat < -60 && lat > -80) {
          value = -10 - yearFactor * 0.3;
          if (lng > -120 && lng < -60) value -= 5; // West Antarctica
          if (lng > 60 && lng < 150) value += 3;   // East Antarctica
        }
        // Amazon water storage
        else if (lat > -20 && lat < 5 && lng > -70 && lng < -50) {
          value = -30 + Math.sin((year - 2000) * 0.3) * 8;
          value += Math.sin((month / 12) * 2 * Math.PI) * 5; // Seasonal
        }
        // Himalayas tectonic uplift
        else if (lat > 25 && lat < 35 && lng > 75 && lng < 95) {
          value = 40 + yearFactor * 0.2;
        }
        // Ocean basins
        else if (Math.abs(lat) < 40 && (lng > 140 || lng < -120)) {
          value = -8 + Math.sin((year - 2000) * 0.1) * 2;
        }
        // Background noise
        else {
          value = (Math.random() - 0.5) * 10;
          value += yearFactor * 0.1;
        }
        
        // Add seasonal variation everywhere
        value += Math.sin((month / 12) * 2 * Math.PI) * 2;
        
        data.push({ 
          lat, 
          lng, 
          value,
          date: `${year}-${month.toString().padStart(2, '0')}`,
          source: 'simulated'
        });
      }
    }
    
    return data;
  }

  // Add this method to your GRACEAPI class (around line 400-450)

/**
 * Fetch multiple months of data with progress callback
 * @param {number} year - Year to fetch
 * @param {string} product - Product type
 * @param {Object} options - Fetch options
 * @param {Function} onProgress - Progress callback (progress, month, completed)
 * @returns {Promise<Array>} Combined monthly data
 */
async fetchYearDataWithProgress(year, product = 'land_mascon', options = {}, onProgress) {
  const monthlyData = [];
  const months = 12;
  let completedMonths = 0;
  
  console.log(`Fetching year ${year} with progress tracking...`);
  
  // Create array of promises
  const promises = [];
  
  for (let month = 1; month <= months; month++) {
    // Check if aborted
    if (options.signal?.aborted) {
      throw new Error('AbortError');
    }
    
    // Create promise for each month
    const promise = this.fetchMonthlyData(year, month, product, options)
      .then(monthData => {
        completedMonths++;
        const progress = Math.round((completedMonths / months) * 100);
        
        // Call progress callback
        if (onProgress) {
          onProgress(progress, month, completedMonths);
        }
        
        console.log(`Month ${month} completed (${completedMonths}/12) - ${progress}%`);
        return { month, data: monthData };
      })
      .catch(error => {
        if (error.name === 'AbortError') throw error;
        console.warn(`Failed to fetch month ${month}:`, error);
        
        completedMonths++;
        const progress = Math.round((completedMonths / months) * 100);
        
        // Still report progress even on failure
        if (onProgress) {
          onProgress(progress, month, completedMonths);
        }
        
        return { month, data: [] };
      });
    
    promises.push(promise);
  }
  
  // Wait for all promises to resolve
  const results = await Promise.all(promises);
  
  // Sort by month and combine
  results.sort((a, b) => a.month - b.month).forEach(item => {
    if (item.data) {
      if (Array.isArray(item.data)) {
        monthlyData.push(...item.data);
      } else if (item.data && typeof item.data === 'object') {
        monthlyData.push(item.data);
      }
    }
  });
  
  console.log(`Year ${year} complete - total data points: ${monthlyData.length}`);
  
  // Cache the year data
  this.cacheYearData(year, monthlyData);
  
  return monthlyData;
}

/**
 * Prefetch and cache all months for a year
 */
async prefetchYear(year, product = 'land_mascon') {
  console.log(`Prefetching all months for year ${year}...`);
  
  // Check if already cached
  const cachedYear = this.getCachedYearData(year);
  if (cachedYear) {
    console.log(`Year ${year} already cached`);
    return cachedYear;
  }
  
  // Use the progress method but ignore progress for prefetch
  return this.fetchYearDataWithProgress(year, product, {}, () => {});
}

/**
 * Fetch multiple months of data (original method)
 */
async fetchYearData(year, product = 'land_mascon', options = {}) {
  return this.fetchYearDataWithProgress(year, product, options, () => {});
}
  /**
   * Average monthly data for annual product
   */
  averageMonthlyData(monthlyData) {
    if (!monthlyData.length) return [];
    
    const grouped = {};
    
    monthlyData.forEach(point => {
      const key = `${point.lat},${point.lng}`;
      if (!grouped[key]) {
        grouped[key] = {
          lat: point.lat,
          lng: point.lng,
          values: [],
          sum: 0
        };
      }
      if (point.value !== undefined && !isNaN(point.value)) {
        grouped[key].values.push(point.value);
        grouped[key].sum += point.value;
      }
    });
    
    return Object.values(grouped).map(item => ({
      lat: item.lat,
      lng: item.lng,
      value: item.values.length > 0 ? item.sum / item.values.length : 0,
      monthlyValues: item.values,
      source: 'averaged'
    }));
  }

  /**
   * Get available years and months for a product
   */
  async getAvailableDates(product = 'land_mascon') {
    // Try to fetch real availability from API
    try {
      const headers = await this.getAuthHeaders();
      const url = `${DATA_SOURCES.TELLUS}/mascon/availability`;
      
      const response = await fetch(url, { headers });
      
      if (response.ok) {
        const data = await response.json();
        return data.dates.map(d => ({
          year: d.year,
          month: d.month,
          available: d.available
        }));
      }
    } catch (error) {
      console.warn('Could not fetch real availability, using estimated dates');
    }
    
    // Fallback to estimated dates
    const dates = [];
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    
    // GRACE-FO data typically has 2-6 month latency
    const latestAvailableYear = currentYear;
    const latestAvailableMonth = Math.max(1, currentMonth - 3);
    
    for (let year = 2002; year <= latestAvailableYear; year++) {
      const maxMonth = (year === latestAvailableYear) ? latestAvailableMonth : 12;
      
      for (let month = 1; month <= maxMonth; month++) {
        // Skip gap between GRACE and GRACE-FO (Oct 2017 - May 2018)
        if (year === 2017 && month > 10) continue;
        if (year === 2018 && month < 5) continue;
        
        // Skip future dates
        if (year === currentYear && month > currentMonth) continue;
        
        dates.push({ year, month });
      }
    }
    
    return dates;
  }

  /**
   * Refresh available dates (check for new data)
   */
  async refreshAvailableDates() {
    // Clear cache to force fresh data
    this.cache.clear();
    
    // Fetch fresh availability
    const dates = await this.getAvailableDates();
    
    // Check if there's newer data than what we had
    const lastDate = dates[dates.length - 1];
    const lastYear = lastDate?.year || 2002;
    const lastMonth = lastDate?.month || 1;
    
    console.log(`Latest available data: ${lastYear}-${lastMonth.toString().padStart(2, '0')}`);
    
    return dates;
  }

  /**
   * Check if specific date has data
   */
  async hasDataForDate(year, month, product = 'land_mascon') {
    const dates = await this.getAvailableDates(product);
    return dates.some(d => d.year === year && d.month === month);
  }

  /**
   * Get data summary statistics
   */
  async getDataSummary(product = 'land_mascon') {
    const dates = await this.getAvailableDates(product);
    
    if (dates.length === 0) {
      return {
        totalMonths: 0,
        firstDate: null,
        lastDate: null,
        completeYears: 0
      };
    }
    
    const firstDate = dates[0];
    const lastDate = dates[dates.length - 1];
    
    // Group by year to find complete years
    const byYear = {};
    dates.forEach(d => {
      if (!byYear[d.year]) byYear[d.year] = [];
      byYear[d.year].push(d.month);
    });
    
    const completeYears = Object.entries(byYear)
      .filter(([year, months]) => months.length === 12)
      .map(([year]) => parseInt(year));
    
    return {
      totalMonths: dates.length,
      firstDate,
      lastDate,
      completeYears,
      percentComplete: (dates.length / ((lastDate.year - 2002 + 1) * 12) * 100).toFixed(1)
    };
  }
  /**
 * Prefetch and cache all months for a year
 */
async prefetchYear(year, product = 'land_mascon') {
  console.log(`Prefetching all months for year ${year}...`);
  
    // Check if already cached
    const cachedYear = this.getCachedYearData(year);
    if (cachedYear) {
        console.log(`Year ${year} already cached`);
        return cachedYear;
    }
    
    const monthlyData = [];
    const months = 12;
    
    // Fetch all months in parallel
    const promises = [];
    for (let month = 1; month <= months; month++) {
        promises.push(
        this.fetchMonthlyData(year, month, product, {})
            .then(data => {
            console.log(`Prefetched ${year}-${month}`);
            return data;
            })
            .catch(err => {
            console.warn(`Failed to prefetch ${year}-${month}:`, err);
            return [];
            })
        );
    }
    
    const results = await Promise.all(promises);
    
    // Combine results
    results.forEach(monthResult => {
        if (Array.isArray(monthResult)) {
        monthlyData.push(...monthResult);
        } else if (monthResult && typeof monthResult === 'object') {
        monthlyData.push(monthResult);
        }
    });
    
    // Cache the year
    this.cacheYearData(year, monthlyData);
    console.log(`Cached ${monthlyData.length} data points for year ${year}`);
    
    return monthlyData;
    }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
    console.log('Cache cleared');
  }

  /**
   * Get cache stats
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
  // Add method to cache entire years
cacheYearData(year, data) {
  this.yearCache.set(year, {
    data: data,
    timestamp: Date.now()
  });
  
  // Limit year cache size
  if (this.yearCache.size > 5) {
    const firstKey = this.yearCache.keys().next().value;
    this.yearCache.delete(firstKey);
  }
}

// Check if year is cached
getCachedYearData(year) {
  const cached = this.yearCache.get(year);
  if (cached && (Date.now() - cached.timestamp) < 3600000) { // 1 hour cache
    return cached.data;
  }
  return null;
}
}

// Create and export singleton instance
const graceAPI = new GRACEAPI();
export default graceAPI;